import React, { useState, useEffect } from 'react';
import { Alert, Box, Button, Modal, Stack } from '@mui/material';
import { FormContainer } from '../../../styles/styleUtils';
import DespesaType from './comp/DespesaType';
import DespesaDetails from './comp/DespesaDetails';
import AtivoImobilizadoType from './comp/AtivoImobilizadoType';

import ActionButtons from '../components/ActionButtons';
import ModalLoading from '../../ModalLoading';
import ComprovanteBaixa from '../components/ComprovanteBaixa';
import { estornarDespesa } from '../../api';

const inferirTipoDespesa = (dados = {}) => {
  if (dados.tipoDespesa) return dados.tipoDespesa;
  if (dados.tipoDespesaRecorrente === true) return 'recorrente';
  if (Number(dados.parcelas || dados.quantidadeParcelas || 1) > 1) return 'parcelada';
  return 'avulsa';
};

const montarEstadoInicial = (dados) => {
  if (dados) {
    const tipoDespesa = inferirTipoDespesa(dados);
    return {
      ...dados,
      tipoDespesa,
      quantidadeParcelas: Number(dados.quantidadeParcelas || dados.parcelas || 1) || 1,
    };
  }

  return {
    data: new Date(),
    codigoDespesa: '',
    despesa: '',
    centroCusto: '',
    vencimento: '',
    valorBaixa: '',
    dataPagamento: '',
    formaPagamento: '',
    contaPagamento: '',
    numeroDocumento: '',
    tipoDespesa: 'avulsa',
    quantidadeParcelas: 1,
    periodicidade: '',
    parcela: 1,
    parcelas: 1,
    tipoAtivoImobilizado: '',
    depreciacao: '',
    vidaUtil: '',
    estadoAtivo: '',
    periodicidadeDepreciacao: '',
    obra: '',
    unidade: '',
  };
};

const NewDespesaForm = React.forwardRef((props, ref) => {
  const [formData, setFormData] = useState(() => montarEstadoInicial(props.formData));
  const [modalLoading, setModalLoading] = useState(false);
  const [disableConfirm, setDisableConfirm] = useState(true);
  const registroJaBaixado = Boolean(props.formData?._id && props.formData?.dataPagamento);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === 'date' && value) {
      setFormData((prev) => ({
        ...prev,
        [name]: new Date(`${value}T00:00:00`),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAutoCompleteChange = (idName, name, value) => {
    setModalLoading(true);
    setFormData((prev) => ({
      ...prev,
      ...(idName ? { [idName]: value?._id || null } : {}),
      [name]: value,
    }));
    setModalLoading(false);
  };

  const handleTypeChange = (event) => {
    const { name, value } = event.target;

    if (name === 'tipoDespesa') {
      setFormData((prev) => {
        // Em edição de registro antigo, altera somente a classificação.
        // Não cria/exclui parcelas e não mexe na baixa já existente.
        if (!props.onInclusao) {
          return {
            ...prev,
            tipoDespesa: value,
          };
        }

        const ehAvulsa = value === 'avulsa';
        const quantidadeAtual = Number(prev.quantidadeParcelas || 0);

        return {
          ...prev,
          tipoDespesa: value,
          quantidadeParcelas: ehAvulsa
            ? 1
            : quantidadeAtual > 1
              ? quantidadeAtual
              : 2,
          periodicidade: ehAvulsa ? '' : prev.periodicidade,
          dataPagamento: ehAvulsa ? prev.dataPagamento : '',
          idFormaPagamento: ehAvulsa ? prev.idFormaPagamento : null,
          formaPagamento: ehAvulsa ? prev.formaPagamento : null,
          idContaPagamento: ehAvulsa ? prev.idContaPagamento : null,
          contaPagamento: ehAvulsa ? prev.contaPagamento : null,
        };
      });
      return;
    }

    const isBoolean = value === 'true' || value === 'false';
    setFormData((prev) => ({
      ...prev,
      [name]: isBoolean ? value === 'true' : value,
    }));
  };

  const handleConfirm = () => {
    const dados = {
      ...formData,
      quantidadeParcelas: Number(formData.quantidadeParcelas || 1),
    };
    props.onConfirm(dados);
  };

  const handleCancel = () => {
    if (window.confirm('Tem certeza que deseja cancelar?')) {
      props.handleClose();
    }
  };

  useEffect(() => {
    const {
      idCentroCusto,
      valorBaixa,
      vencimento,
      unidade,
      tipoDespesa,
      quantidadeParcelas,
      periodicidade,
      dataPagamento,
      idFormaPagamento,
      idContaPagamento,
    } = formData;

    const valorValido = Number(valorBaixa) > 0;
    const areRequiredFieldsFilled = idCentroCusto && valorValido && vencimento && unidade && tipoDespesa;

    let specificValidationsMet = true;

    if (
      props.onInclusao &&
      (tipoDespesa === 'parcelada' || tipoDespesa === 'recorrente') &&
      (Number(quantidadeParcelas) < 2 || !periodicidade)
    ) {
      specificValidationsMet = false;
    }

    if (dataPagamento && !idContaPagamento) {
      specificValidationsMet = false;
    }

    if (dataPagamento && !idFormaPagamento) {
      specificValidationsMet = false;
    }

    setDisableConfirm(!(areRequiredFieldsFilled && specificValidationsMet));
  }, [formData, props.onInclusao]);

  const handleComprovanteUpload = (upload) => {
    setFormData((prev) => ({
      ...prev,
      comprovanteBaixaUpload: upload,
    }));
  };

  const handleEstorno = async () => {
    const motivo = window.prompt('Informe o motivo do estorno do pagamento:');
    if (!motivo || motivo.trim().length < 3) {
      window.alert('Informe um motivo válido para o estorno.');
      return;
    }

    setModalLoading(true);
    const result = await estornarDespesa(formData._id, motivo.trim());
    setModalLoading(false);

    if (result?.success === false || result?.ok === false) {
      window.alert(result?.message || 'Não foi possível estornar o pagamento.');
      return;
    }

    window.alert(result?.message || 'Pagamento estornado com sucesso.');
    props.onEstornoConcluido?.();
    props.handleClose();
  };

  return (
    <Modal open={props.open} onClose={props.handleClose}>
      <FormContainer>
        {registroJaBaixado && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta despesa já foi paga e está bloqueada para edição. Para corrigir o lançamento, estorne a baixa primeiro.
          </Alert>
        )}

        <Box sx={registroJaBaixado ? { pointerEvents: 'none', opacity: 0.65 } : undefined}>
        <DespesaType
          tipoDespesa={formData.tipoDespesa}
          onTypeChange={handleTypeChange}
          disabled={Boolean(formData.idVenda)}
        />

        <DespesaDetails
          formData={formData}
          onInputChange={handleInputChange}
          onAutoCompleteChange={handleAutoCompleteChange}
          onInclusao={props.onInclusao}
        />

        {formData.centroCusto?.nome === 'Ativo Imobilizado' && (
          <AtivoImobilizadoType
            formData={formData}
            onInputChange={handleInputChange}
            tipoAtivoImobilizado={formData.tipoAtivoImobilizado}
            onTypeChange={handleTypeChange}
            disabled={formData.visualiza}
          />
        )}

        </Box>

        {(formData.dataPagamento || formData.comprovanteBaixa || formData.comprovantesHistoricos?.length) && (
          <ComprovanteBaixa
            formData={formData}
            onUploadChange={handleComprovanteUpload}
            podeAnexar={!registroJaBaixado && Boolean(formData.dataPagamento)}
            titulo="Comprovante do pagamento"
          />
        )}

        {registroJaBaixado ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={props.handleClose}>Fechar</Button>
            <Button color="warning" variant="contained" onClick={handleEstorno}>
              Estornar baixa
            </Button>
          </Stack>
        ) : (
          <ActionButtons onConfirm={handleConfirm} onCancel={handleCancel} disableConfirm={disableConfirm} />
        )}
        {modalLoading && <ModalLoading open={modalLoading} />}
      </FormContainer>
    </Modal>
  );
});

export default NewDespesaForm;
